import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Trash2, Edit2, Check, X, FileText, Image, Tag, Calendar, 
  Eye, EyeOff, ChevronDown, ChevronUp, Search, Filter,
  Bold, Italic, Heading1, Heading2, Heading3, List, ListOrdered, Quote, Code, Link2, ImagePlus, Minus
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Markdown Toolbar Component
const MarkdownToolbar = ({ textareaRef, value, onChange }) => {
  const insertMarkdown = (before, after = '', placeholder = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newValue = 
      value.substring(0, start) + 
      before + textToInsert + after + 
      value.substring(end);
    
    onChange(newValue);
    
    // Set cursor position after insert
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length + after.length;
      textarea.setSelectionRange(
        selectedText ? newCursorPos : start + before.length,
        selectedText ? newCursorPos : start + before.length + placeholder.length
      );
    }, 0);
  };

  const tools = [
    { icon: Bold, label: 'Bold', action: () => insertMarkdown('**', '**', 'bold text') },
    { icon: Italic, label: 'Italic', action: () => insertMarkdown('*', '*', 'italic text') },
    { type: 'divider' },
    { icon: Heading1, label: 'Heading 1', action: () => insertMarkdown('\n# ', '\n', 'Heading 1') },
    { icon: Heading2, label: 'Heading 2', action: () => insertMarkdown('\n## ', '\n', 'Heading 2') },
    { icon: Heading3, label: 'Heading 3', action: () => insertMarkdown('\n### ', '\n', 'Heading 3') },
    { type: 'divider' },
    { icon: List, label: 'Bullet List', action: () => insertMarkdown('\n- ', '', 'List item') },
    { icon: ListOrdered, label: 'Numbered List', action: () => insertMarkdown('\n1. ', '', 'List item') },
    { icon: Quote, label: 'Quote', action: () => insertMarkdown('\n> ', '\n', 'Quote text') },
    { type: 'divider' },
    { icon: Code, label: 'Code', action: () => insertMarkdown('`', '`', 'code') },
    { icon: Link2, label: 'Link', action: () => insertMarkdown('[', '](url)', 'link text') },
    { icon: ImagePlus, label: 'Image', action: () => insertMarkdown('![', '](image-url)', 'alt text') },
    { type: 'divider' },
    { icon: Minus, label: 'Horizontal Line', action: () => insertMarkdown('\n---\n', '', '') },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-100 border border-gray-200 rounded-t-lg border-b-0">
      {tools.map((tool, index) => 
        tool.type === 'divider' ? (
          <div key={index} className="w-px h-6 bg-gray-300 mx-1" />
        ) : (
          <button
            key={index}
            type="button"
            onClick={tool.action}
            className="p-1.5 rounded hover:bg-gray-200 text-gray-600 hover:text-[#320547] transition-colors"
            title={tool.label}
          >
            <tool.icon size={16} />
          </button>
        )
      )}
      <span className="ml-auto text-xs text-gray-400">Markdown supported</span>
    </div>
  );
};

const BlogPanel = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [services, setServices] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const contentTextareaRef = useRef(null);
  const editContentTextareaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    tags: '',
    image: '',
    excerpt: '',
    content: '',
    author: '',
    author_image: '',
    is_published: true,
    related_services: [],
    related_industries: []
  });

  const categories = ['Technology', 'Business', 'Digital Transformation', 'AI & ML', 'Cloud', 'Security', 'Innovation'];

  useEffect(() => {
    fetchPosts();
    fetchServices();
    fetchIndustries();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts`);
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/service-menu`);
      if (res.ok) {
        const data = await res.json();
        setServices(data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/industry-menu`);
      if (res.ok) {
        const data = await res.json();
        setIndustries(data);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      category: '',
      tags: '',
      image: '',
      excerpt: '',
      content: '',
      author: '',
      author_image: '',
      is_published: true,
      related_services: [],
      related_industries: []
    });
  };

  const handleAdd = async () => {
    if (!formData.title || !formData.content || !formData.excerpt) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        author: formData.author || 'Navaris Digital',
        author_image: formData.author_image || 'https://customer-assets.emergentagent.com/job_mobittech/artifacts/hyzx3sw9_Frame%202%20%281%29.png',
        related_services: formData.related_services || [],
        related_industries: formData.related_industries || []
      };

      const res = await fetch(`${API_URL}/api/blog/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setPosts([data, ...posts]);
        resetForm();
        setIsAdding(false);
        toast.success('Blog post created successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create blog post');
      }
    } catch (error) {
      console.error('Error creating blog post:', error);
      toast.error('Failed to create blog post');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const payload = {
        ...formData,
        tags: typeof formData.tags === 'string' 
          ? formData.tags.split(',').map(t => t.trim()).filter(t => t)
          : formData.tags,
        related_services: formData.related_services || [],
        related_industries: formData.related_industries || []
      };

      const res = await fetch(`${API_URL}/api/blog/posts/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const updated = await res.json();
        setPosts(posts.map(p => p.id === id ? updated : p));
        setEditingId(null);
        resetForm();
        toast.success('Blog post updated successfully');
      } else {
        toast.error('Failed to update blog post');
      }
    } catch (error) {
      console.error('Error updating blog post:', error);
      toast.error('Failed to update blog post');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (res.ok) {
        setPosts(posts.filter(p => p.id !== id));
        toast.success('Blog post deleted successfully');
      } else {
        toast.error('Failed to delete blog post');
      }
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error('Failed to delete blog post');
    }
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setFormData({
      title: post.title,
      slug: post.slug,
      category: post.category || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : post.tags || '',
      image: post.image || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      author: post.author || '',
      author_image: post.author_image || '',
      is_published: post.is_published !== false,
      related_services: post.related_services || [],
      related_industries: post.related_industries || []
    });
    setExpandedId(post.id);
  };

  const toggleService = (serviceId) => {
    const current = formData.related_services || [];
    if (current.includes(serviceId)) {
      setFormData({ ...formData, related_services: current.filter(id => id !== serviceId) });
    } else {
      setFormData({ ...formData, related_services: [...current, serviceId] });
    }
  };

  const toggleIndustry = (industryId) => {
    const current = formData.related_industries || [];
    if (current.includes(industryId)) {
      setFormData({ ...formData, related_industries: current.filter(id => id !== industryId) });
    } else {
      setFormData({ ...formData, related_industries: [...current, industryId] });
    }
  };

  const getServiceName = (id) => services.find(s => s.id === id)?.name || id;
  const getIndustryName = (id) => industries.find(i => i.id === id)?.name || id;

  const togglePublish = async (post) => {
    try {
      const res = await fetch(`${API_URL}/api/blog/posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`
        },
        body: JSON.stringify({ is_published: !post.is_published })
      });

      if (res.ok) {
        const updated = await res.json();
        setPosts(posts.map(p => p.id === post.id ? updated : p));
        toast.success(`Post ${updated.is_published ? 'published' : 'unpublished'}`);
      }
    } catch (error) {
      console.error('Error toggling publish:', error);
      toast.error('Failed to update post status');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || post.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#320547] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#320547]">Blog Posts</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage your blog content and articles
          </p>
        </div>
        <Button
          onClick={() => { setIsAdding(true); resetForm(); }}
          className="bg-[#320547] hover:bg-[#95228A] text-white"
          disabled={isAdding}
        >
          <Plus size={16} className="mr-2" />
          New Post
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#320547]"
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Add New Post Form */}
      <AnimatePresence>
        {isAdding && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <Card className="border-[#320547]/20">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-[#320547]">Create New Blog Post</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <Input
                      placeholder="Enter post title"
                      value={formData.title}
                      onChange={handleTitleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                    <Input
                      placeholder="post-url-slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#320547]"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                    <Input
                      placeholder="tag1, tag2, tag3"
                      value={formData.tags}
                      onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image URL</label>
                  <Input
                    placeholder="https://example.com/image.jpg"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt *</label>
                  <Textarea
                    placeholder="Brief summary of the post (shown in listings)"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                  <MarkdownToolbar 
                    textareaRef={contentTextareaRef}
                    value={formData.content}
                    onChange={(newContent) => setFormData({ ...formData, content: newContent })}
                  />
                  <Textarea
                    ref={contentTextareaRef}
                    placeholder="Write your blog post content here..."
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    rows={12}
                    className="font-mono text-sm rounded-t-none border-t-0"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
                    <Input
                      placeholder="Author name"
                      value={formData.author}
                      onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Author Image URL</label>
                    <Input
                      placeholder="https://example.com/author.jpg"
                      value={formData.author_image}
                      onChange={(e) => setFormData({ ...formData, author_image: e.target.value })}
                    />
                  </div>
                </div>

                {/* Related Services Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Related Services <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[44px]">
                    {services.length === 0 ? (
                      <span className="text-sm text-gray-400">No services available</span>
                    ) : (
                      services.map(service => (
                        <button
                          key={service.id}
                          type="button"
                          onClick={() => toggleService(service.id)}
                          className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                            formData.related_services?.includes(service.id)
                              ? 'bg-[#320547] text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-[#320547] hover:text-[#320547]'
                          }`}
                        >
                          {service.name}
                        </button>
                      ))
                    )}
                  </div>
                  {formData.related_services?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{formData.related_services.length} service(s) selected</p>
                  )}
                </div>

                {/* Related Industries Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Related Industries <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[44px]">
                    {industries.length === 0 ? (
                      <span className="text-sm text-gray-400">No industries available</span>
                    ) : (
                      industries.map(industry => (
                        <button
                          key={industry.id}
                          type="button"
                          onClick={() => toggleIndustry(industry.id)}
                          className={`px-3 py-1.5 text-sm rounded-full transition-all ${
                            formData.related_industries?.includes(industry.id)
                              ? 'bg-[#95228A] text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-[#95228A] hover:text-[#95228A]'
                          }`}
                        >
                          {industry.name}
                        </button>
                      ))
                    )}
                  </div>
                  {formData.related_industries?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{formData.related_industries.length} industry(ies) selected</p>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={handleAdd} className="bg-[#320547] hover:bg-[#95228A]">
                    <Check size={16} className="mr-2" />
                    Publish Post
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setIsAdding(false); resetForm(); }}
                  >
                    <X size={16} className="mr-2" />
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Posts List */}
      <div className="space-y-3">
        {filteredPosts.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">No blog posts yet</h3>
              <p className="text-gray-500 mb-4">
                Create your first blog post to share insights and engage your audience.
              </p>
              <Button
                onClick={() => setIsAdding(true)}
                className="bg-[#320547] hover:bg-[#95228A]"
              >
                <Plus size={16} className="mr-2" />
                Create First Post
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredPosts.map((post) => (
            <motion.div key={post.id} layout>
              <Card className={`${post.is_published === false ? 'opacity-60 border-dashed' : ''}`}>
                <CardContent className="py-4">
                  {editingId === post.id ? (
                    /* Edit Mode */
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <Input
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                          <Input
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          >
                            <option value="">Select category</option>
                            {categories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                          <Input
                            value={formData.tags}
                            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Featured Image</label>
                        <Input
                          value={formData.image}
                          onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                        <Textarea
                          value={formData.excerpt}
                          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                          rows={2}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                        <MarkdownToolbar 
                          textareaRef={editContentTextareaRef}
                          value={formData.content}
                          onChange={(newContent) => setFormData({ ...formData, content: newContent })}
                        />
                        <Textarea
                          ref={editContentTextareaRef}
                          value={formData.content}
                          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                          rows={10}
                          className="font-mono text-sm rounded-t-none border-t-0"
                        />
                      </div>

                      {/* Author Info - Edit Mode */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
                          <Input
                            placeholder="Author name"
                            value={formData.author}
                            onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Author Image URL</label>
                          <Input
                            placeholder="https://example.com/author.jpg"
                            value={formData.author_image}
                            onChange={(e) => setFormData({ ...formData, author_image: e.target.value })}
                          />
                        </div>
                      </div>

                      {/* Related Services - Edit Mode */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Related Services</label>
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[40px]">
                          {services.map(service => (
                            <button
                              key={service.id}
                              type="button"
                              onClick={() => toggleService(service.id)}
                              className={`px-3 py-1 text-xs rounded-full transition-all ${
                                formData.related_services?.includes(service.id)
                                  ? 'bg-[#320547] text-white'
                                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#320547]'
                              }`}
                            >
                              {service.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Related Industries - Edit Mode */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Related Industries</label>
                        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200 min-h-[40px]">
                          {industries.map(industry => (
                            <button
                              key={industry.id}
                              type="button"
                              onClick={() => toggleIndustry(industry.id)}
                              className={`px-3 py-1 text-xs rounded-full transition-all ${
                                formData.related_industries?.includes(industry.id)
                                  ? 'bg-[#95228A] text-white'
                                  : 'bg-white border border-gray-200 text-gray-600 hover:border-[#95228A]'
                              }`}
                            >
                              {industry.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdate(post.id)}
                          className="bg-[#320547] hover:bg-[#95228A]"
                        >
                          <Check size={14} className="mr-1" />
                          Save Changes
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setEditingId(null); resetForm(); }}
                        >
                          <X size={14} className="mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div>
                      <div className="flex items-start gap-4">
                        {post.image && (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 truncate">{post.title}</h3>
                            {post.is_published === false && (
                              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-700 rounded">
                                Draft
                              </span>
                            )}
                            {post.category && (
                              <span className="px-2 py-0.5 text-xs bg-[#320547]/10 text-[#320547] rounded">
                                {post.category}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{post.excerpt}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(post.created_at).toLocaleDateString()}
                            </span>
                            {post.tags?.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Tag size={12} />
                                {post.tags.slice(0, 3).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => togglePublish(post)}
                            className="text-gray-500"
                            title={post.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {post.is_published !== false ? (
                              <Eye size={16} className="text-green-500" />
                            ) : (
                              <EyeOff size={16} className="text-gray-400" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setExpandedId(expandedId === post.id ? null : post.id)}
                            className="text-gray-500"
                          >
                            {expandedId === post.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(post)}
                            className="text-gray-500 hover:text-[#320547]"
                          >
                            <Edit2 size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(post.id)}
                            className="text-gray-500 hover:text-red-500"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      {/* Expanded Content Preview */}
                      <AnimatePresence>
                        {expandedId === post.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mt-4 pt-4 border-t border-gray-100"
                          >
                            <div className="bg-gray-50 rounded-lg p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Content Preview:</h4>
                              <div className="text-sm text-gray-600 whitespace-pre-wrap max-h-60 overflow-y-auto">
                                {post.content?.substring(0, 500)}
                                {post.content?.length > 500 && '...'}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-500">
                              <span>Author: {post.author || 'Unknown'}</span>
                              <span>Slug: /{post.slug}</span>
                            </div>
                            
                            {/* Related Services & Industries */}
                            {(post.related_services?.length > 0 || post.related_industries?.length > 0) && (
                              <div className="mt-3 pt-3 border-t border-gray-100">
                                {post.related_services?.length > 0 && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-500 mr-2">Services:</span>
                                    {post.related_services.map(id => (
                                      <span key={id} className="inline-block px-2 py-0.5 text-xs bg-[#320547]/10 text-[#320547] rounded mr-1">
                                        {getServiceName(id)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {post.related_industries?.length > 0 && (
                                  <div>
                                    <span className="text-xs font-medium text-gray-500 mr-2">Industries:</span>
                                    {post.related_industries.map(id => (
                                      <span key={id} className="inline-block px-2 py-0.5 text-xs bg-[#95228A]/10 text-[#95228A] rounded mr-1">
                                        {getIndustryName(id)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Stats */}
      {posts.length > 0 && (
        <div className="flex gap-4 text-sm text-gray-500">
          <span>{posts.length} total posts</span>
          <span>{posts.filter(p => p.is_published !== false).length} published</span>
          <span>{posts.filter(p => p.is_published === false).length} drafts</span>
        </div>
      )}
    </div>
  );
};

export default BlogPanel;
